import React from 'react';

import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { Field, Form } from '@hrlogics/react-components/form';

import { appRoutes } from '@clearverify/constants';

import { useInviteLazyQuery, useUserRegistrationByInviteMutation } from 'src/api';
import { NOTICE_TO_FURNISHERS } from 'src/assets';
import { Button, PdfRenderer } from 'src/components/common';
import { CheckboxField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useAuthSignup } from 'src/hooks/auth';
import { useInviteParamId } from 'src/hooks/invite';
import type { SxProp } from 'src/types/mui';
import { validateWithYupSchema } from 'src/utils/yup';

import { NoticeToFurnishersFormFields, NoticeToFurnishersFormValues } from '../../types';
import { noticeToFurnishersFormValidationSchema } from '../../utils';

export const NoticeToFurnishersForm: React.FC = () => {
  const navigate = useNavigate();

  const { formData, inviteData, goToNextStep, prepareFormData } = useAuthSignup();
  const { setAuthState } = useAppAuth();
  const { createApolloContext } = useNotification();
  const inviteId = useInviteParamId();

  const [signUpUserByInvite] = useUserRegistrationByInviteMutation({
    context: createApolloContext(),
  });

  const [getInviteData] = useInviteLazyQuery({
    variables: {
      id: inviteId || '',
    },
    fetchPolicy: 'network-only',
  });
  const handleSubmit = React.useCallback(async () => {
    try {
      const { data } = await getInviteData();
      const formDataResult = prepareFormData(String(inviteData?.id), formData);
      if (
        data?.getInvite.isAdditionalStaff &&
        (formDataResult.peo?.displayName ||
          formDataResult.employer?.displayName ||
          formDataResult.peoPayrollProvider?.displayName ||
          formDataResult.verifier?.companyDisplayName)
      ) {
        goToNextStep();
      } else {
        const { data } = await signUpUserByInvite({
          variables: formDataResult,
        });
        await setAuthState({
          bearerToken: String(data?.userRegistrationByInvite.idToken),
          accessToken: data?.userRegistrationByInvite.accessToken ?? undefined,
        });
        navigate(appRoutes.auth.setupMfa);
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    getInviteData,
    prepareFormData,
    inviteData?.id,
    formData,
    goToNextStep,
    signUpUserByInvite,
    setAuthState,
    navigate,
  ]);

  return (
    <Box>
      <Typography variant="body2" fontSize="24px">
        Notice to Furnishers
      </Typography>
      <Typography variant="body2" fontSize="16px" mt={1}>
        Please read the Notice to Furnishers document below and acknowledge once you have finished
        reviewing.
      </Typography>
      <Box mt={2}>
        <PdfRenderer src={NOTICE_TO_FURNISHERS} />
      </Box>
      <Form<NoticeToFurnishersFormValues>
        onSubmit={handleSubmit}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(noticeToFurnishersFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Field
                component={CheckboxField}
                name={NoticeToFurnishersFormFields.isRulesRead}
                label="I acknowledge that I have read, understand, and agree to the Notice to Furnishers"
                fullWidth
                required
              />

              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  sx={submitButtonStyles}
                  disabled={submitting || !valid}
                  loading={submitting}
                  size="large"
                  variant="contained"
                >
                  Submit
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};
